import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromCatalogPlan from '../reducers/catalog-plan.reducer';

export const selectCatalogPlanState = createFeatureSelector<fromCatalogPlan.State>(
  fromCatalogPlan.catalogPlanFeatureKey,
);
export const selectSourcePage = createSelector(
  selectCatalogPlanState,
  (state) => state.sourcePage
);
export const selectPlanBackground = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.planBackground,
);
export const selectPlanData = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.planData,
);
export const selectEventSessionId = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.eventSessionId,
);
export const selectBlockViewData = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.blockViewData,
);
export const selectAllContingentImages = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.allContingentImages,
);
export const selectSeatsStampVersion = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.seatsStampVersion,
);
export const selectPlanType = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.planType,
);
export const isPlanEmpty = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) =>
    state?.eventSessionId &&
    (!state?.planData?.length || !state.planBackground) &&
    !state?.blockViewData &&
    !state.isPlanLoading,
);
export const isPlanLoading = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state.isPlanLoading,
);
export const selectSelectionType = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state.selectionType,
);
export const selectLegend = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state.legend,
);
export const selectBlockId = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.blockId,
);
export const selectPlan = createSelector(
  selectEventSessionId,
  selectPlanBackground,
  selectPlanData,
  isPlanLoading,
  selectBlockViewData,
  selectBlockId,
  (eventSessionId, planBackground, planData, loading, blockViewData, blockId) => ({
    eventSessionId,
    planBackground,
    planData,
    loading,
    blockViewData,
    blockId,
  }),
);
export const selectEventDetails = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.eventDetails,
);

export const selectSeatSelection = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.seatSelection,
);
export const selectPlanIds = createSelector(
  selectBlockId,
  selectEventSessionId,
  selectPlanType,
  (blockId, eventSessionId, planType) => ({
    blockId,
    eventSessionId,
    planType,
  }),
);
export const selectContingentsFilterList = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.contingentsFilterList,
);
export const selectCategoriesFilterList = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.categoriesFilterList,
);
export const selectUnavailableSeatTypesFilterList = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.unavailableSeatTypesFilterList,
);
export const selectCategoryIdsFilterSelected = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.categoryIdsFilterSelected,
);
export const selectContingentIdsFilterSelected = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.contingentIdsFilterSelected,
);
export const selectUnavailableIdsFilterSelected = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.unavailableSeatTypeIdsSelected,
);
export const selectMultiQuickFilterSelections = createSelector(
  selectCatalogPlanState,
  (state) => state.multiQuickFilter,
);
export const selectEventSessionCategoriesFilter = createSelector(
  selectEventSessionId,
  selectCategoryIdsFilterSelected,
  (eventSessionId, categoryIds) => ({ eventSessionId, categoriesFilter: categoryIds }),
);
export const selectEventSessionContingentsFilter = createSelector(
  selectEventSessionId,
  selectContingentIdsFilterSelected,
  (eventSessionId, contingentIds) => ({ eventSessionId, contingentsFilter: contingentIds }),
);
export const selectEventSessionPlanFilter = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => ({
    eventSessionId: state.eventSessionId,
    contingentsFilter: state.contingentIdsFilterSelected,
    categoriesFilter: state.categoryIdsFilterSelected,
    unavailableSeatTypesFilter: state.unavailableSeatTypeIdsSelected,
    seatStatusIdsFilter: state.seatStatusIdsFilterSelected,
  }),
);
export const applyPlanFilter = createSelector(selectCatalogPlanState, (state: fromCatalogPlan.State) => ({
  planType: state.planType,
  eventSessionId: state.eventSessionId,
  contingentsFilter: state.contingentIdsFilterSelected,
  categoriesFilter: state.categoryIdsFilterSelected,
  unavailableSeatTypesFilter: state.unavailableSeatTypeIdsSelected,
  seatStatusesFilter: state.seatStatusIdsFilterSelected,
}));
export const selectSeatStatusOptionFilterSelected = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.seatStatusOptionFilterSelected,
);
export const selectSeatColorsFilter = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.seatColorsFilter,
);
export const selectAllCategories = createSelector(selectCatalogPlanState, (state) => state?.allCategories);
export const selectAllContingents = createSelector(selectCatalogPlanState, (state) => state?.allContingents);
export const selectAllUnavailableSeats = createSelector(
  selectCatalogPlanState,
  (state) => state?.allUnavailableSeats,
);
export const selectSelectedChanges = createSelector(
  selectCatalogPlanState,
  (state) => state.selectedSeatChanges,
);

export const selectBulkSelectionStats = createSelector(selectCatalogPlanState, (state) => state.bulkSelectionStats);
export const selectBulkSelectionScope = createSelector(selectCatalogPlanState, (state) => state.bulkSelectionScope);
// ⚠ SANS LECTEUR a ce jour, volontairement : brancher le bandeau de recap sur cette tranche suppose
// de deriver le compteur « disponibles » par soustraction (le DTO n'a pas de champ availableSeats), ce
// qui change le sens du chiffre affiche -> arbitrage produit, pas technique. Ne pas supprimer sans
// trancher : c'est la seule donnee « bloc courant + filtre courant » du store, et rien ne peut s'y
// substituer. Raisonnement complet sur l'effet loadBlockScopedFilterStatsOnBlockEntry$
// (catalog-plan.effects.ts). Signale en review MR !200.
export const selectBlockScopedFilterStats = createSelector(
  selectCatalogPlanState,
  (state) => state.blockScopedFilterStats,
);
export const hasPendingChanges = createSelector(
  selectSeatSelection,
  selectSelectedChanges,
  selectBulkSelectionStats,
  (selectedSeats, selectedChanges, selectionStats) =>
    (selectedSeats.length > 0 || selectionStats != null) &&
    (selectedChanges.categoryId !== null ||
      selectedChanges.contingentId !== null ||
      selectedChanges.unavailabilityId !== null),
);

export const selectBlockCategories = createSelector(selectCatalogPlanState, (state) => state.blockCategories);
export const selectBlockContingents = createSelector(
  selectCatalogPlanState,
  (state) => state.blockContingents,
);
export const selectBlockUnavailableSeats = createSelector(
  selectCatalogPlanState,
  (state) => state.blockUnavailableSeats,
);
export const selectViewSeatStatusCounts = createSelector(selectCatalogPlanState, (state) => state.viewSeatStatusStats);
export const selectViewMultiSelectionStats = createSelector(selectCatalogPlanState, (state) => state.viewMultiSelectionStats);
export const selectPlanWideSeatStatusCounts = createSelector(selectCatalogPlanState, (state) => state.planWideSeatStatusStats);
export const selectFilteredSeatStatusStats = createSelector(selectCatalogPlanState, (state) => state.filteredSeatStatusStats);
export const selectSeatStatusIdsFilterSelected = createSelector(
  selectCatalogPlanState,
  (state: fromCatalogPlan.State) => state?.seatStatusIdsFilterSelected,
);
